import React from 'react';
import { Link } from 'react-router-dom';
import { useShellI18n } from '@/features/language/useI18n';

const EFFECTIVE_DATE = '2026-06-19';

const ContactDetails = ({ language }: { language: 'de' | 'en' }) => (
  <address className="legal-contact">
    <strong>WIANCO OTT Robotics GmbH</strong>
    <br />
    Schloßpark 1
    <br />
    64342 Seeheim-Jugenheim
    <br />
    {language === 'de' ? 'Deutschland' : 'Germany'}
    <br />
    {language === 'de' ? 'E-Mail' : 'Email'}: <a href="mailto:info@wianco.com">info@wianco.com</a>
  </address>
);

const DataProtectionOfficer = ({ language }: { language: 'de' | 'en' }) => (
  <address className="legal-contact">
    <strong>heyData GmbH</strong>
    <br />
    Schützenstraße 5
    <br />
    10117 Berlin
    <br />
    {language === 'de' ? 'E-Mail' : 'Email'}:{' '}
    <a href="mailto:datenschutz@heydata.eu">datenschutz@heydata.eu</a>
    <br />
    <a href="https://heydata.eu" target="_blank" rel="noreferrer">
      heydata.eu
    </a>
  </address>
);

const GermanPolicy = () => (
  <>
    <section className="legal-section" aria-labelledby="scope-heading">
      <h2 id="scope-heading">1. Geltungsbereich und Einleitung</h2>
      <p>
        Diese Datenschutzerklärung informiert Sie gemäß Art. 12 bis 14 der
        Datenschutz-Grundverordnung (DSGVO) über die Verarbeitung personenbezogener Daten bei der
        Nutzung des WIANCO Portals unter portal.ottrobotics.de einschließlich der darin
        eingebundenen Dienste Community, Academy, LaunchPad, Support und Shop.
      </p>
      <p>
        Personenbezogene Daten sind alle Informationen, die sich auf eine identifizierte oder
        identifizierbare natürliche Person beziehen, beispielsweise Name, E-Mail-Adresse,
        Benutzerkennung oder IP-Adresse.
      </p>
    </section>

    <section className="legal-section" aria-labelledby="controller-heading">
      <h2 id="controller-heading">2. Verantwortlicher und Datenschutzbeauftragter</h2>
      <h3>2.1 Verantwortlicher</h3>
      <ContactDetails language="de" />
      <p>Vertreten durch die Geschäftsführung Michael Wilczynska und Paul Tessmann.</p>
      <h3>2.2 Datenschutzbeauftragter</h3>
      <DataProtectionOfficer language="de" />
    </section>

    <section className="legal-section" aria-labelledby="legal-bases-heading">
      <h2 id="legal-bases-heading">3. Zwecke und Rechtsgrundlagen</h2>
      <p>
        Wir verarbeiten personenbezogene Daten nur, soweit hierfür eine Rechtsgrundlage besteht:
      </p>
      <ul className="legal-list">
        <li>
          <strong>Einwilligung (Art. 6 Abs. 1 lit. a DSGVO):</strong> für einwilligungsbedürftige
          Funktionen. Eine Einwilligung kann jederzeit mit Wirkung für die Zukunft widerrufen
          werden.
        </li>
        <li>
          <strong>Vertrag und vorvertragliche Maßnahmen (Art. 6 Abs. 1 lit. b DSGVO):</strong> für
          die Bereitstellung des Portalkontos, vertraglich vereinbarter Dienste, Supportleistungen
          und Bestellanfragen.
        </li>
        <li>
          <strong>Rechtliche Verpflichtung (Art. 6 Abs. 1 lit. c DSGVO):</strong> insbesondere für
          handels-, steuer- oder aufsichtsrechtliche Pflichten.
        </li>
        <li>
          <strong>Berechtigte Interessen (Art. 6 Abs. 1 lit. f DSGVO):</strong> für einen sicheren
          und stabilen Portalbetrieb, Missbrauchsabwehr, Fehleranalyse, Berechtigungsverwaltung,
          bedarfsgerechte Kundenbetreuung sowie die Geltendmachung und Abwehr von Ansprüchen.
        </li>
      </ul>
      <p>
        Soweit Informationen auf Ihrem Endgerät gespeichert oder ausgelesen werden, erfolgt dies
        nach § 25 TDDDG. Technisch unbedingt erforderliche Vorgänge stützen wir auf § 25 Abs. 2 Nr.
        2 TDDDG; im Übrigen holen wir vorab eine Einwilligung nach § 25 Abs. 1 TDDDG ein.
      </p>
    </section>

    <section className="legal-section" aria-labelledby="technical-heading">
      <h2 id="technical-heading">4. Aufruf des Portals, Hosting und Protokolldaten</h2>
      <p>
        Bei jedem Aufruf übermittelt Ihr Browser technisch erforderliche Daten an unsere Systeme.
        Dazu können IP-Adresse, Datum und Uhrzeit, aufgerufene URL, Referrer-URL, HTTP-Status,
        übertragene Datenmenge, Browsertyp und -version, Betriebssystem, Sprache sowie
        Geräteinformationen gehören.
      </p>
      <p>
        Die Verarbeitung dient der Auslieferung des Portals, der Stabilität, IT-Sicherheit,
        Fehlerdiagnose und Missbrauchsabwehr. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser
        berechtigtes Interesse liegt im sicheren und zuverlässigen Betrieb des Portals. Operative
        Telemetrie wird in der Produktionsumgebung grundsätzlich bis zu 30 Tage vorgehalten. Bei
        einem Sicherheitsvorfall können relevante Daten bis zur Klärung und darüber hinaus im Rahmen
        gesetzlicher Verjährungsfristen gespeichert werden.
      </p>
      <p>
        Das Portal wird auf Microsoft Azure in Rechenzentren der Europäischen Union betrieben. Dabei
        nutzen wir insbesondere Azure Kubernetes Service, Azure Database for PostgreSQL, Azure
        Files, Azure Blob Storage, Azure Key Vault sowie Azure Monitor/Log Analytics. Empfänger ist
        als Auftragsverarbeiter Microsoft Ireland Operations Limited, One Microsoft Place, South
        County Business Park, Leopardstown, Dublin 18, Irland.
      </p>
    </section>

    <section className="legal-section" aria-labelledby="account-heading">
      <h2 id="account-heading">5. Benutzerkonto, Anmeldung und Berechtigungen</h2>
      <p>
        Für die Anmeldung und Zugriffskontrolle nutzen wir eine von uns betriebene Keycloak-Instanz.
        Verarbeitet werden insbesondere Benutzerkennung, Name, geschäftliche E-Mail-Adresse,
        Anmeldedaten in gehashter Form, Rollen, Gruppen, Berechtigungen, Sitzungs- und Token-Daten,
        Anmeldezeitpunkte sowie sicherheitsrelevante Ereignisse. Die Daten sind erforderlich, um Sie
        zu authentifizieren, Zugriffsrechte durchzusetzen und Ihr Konto zu schützen.
      </p>
      <p>
        Zur Prüfung und Aktualisierung Ihrer Portalberechtigungen gleichen wir Ihre geschäftliche
        E-Mail-Adresse sowie Kontakt-, Unternehmens-, Kunden- und Partnerstatus mit HubSpot ab. Bei
        der erstmaligen Zugangsanforderung verwenden wir Ihre E-Mail-Adresse außerdem, um die
        Berechtigung zu prüfen und eine Einladungs- oder Anmelde-E-Mail zu versenden.
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO; soweit die Berechtigungs- und
        Missbrauchskontrolle über das vertraglich Erforderliche hinausgeht, Art. 6 Abs. 1 lit. f
        DSGVO.
      </p>
      <p>
        Pflichtangaben sind als solche erkennbar. Ohne die für Anmeldung und Berechtigungsprüfung
        erforderlichen Daten können wir den Portalzugang nicht bereitstellen.
      </p>
      <p>
        Konten- und Berechtigungsdaten erhalten wir von Ihnen selbst, von dem Unternehmen, das Ihren
        Zugang veranlasst hat, sowie aus unseren Identitäts- und CRM-Systemen Keycloak und HubSpot.
      </p>
    </section>

    <section className="legal-section" aria-labelledby="features-heading">
      <h2 id="features-heading">6. Verarbeitung in den Portal-Diensten</h2>

      <h3>6.1 Profil und Kontokontext</h3>
      <p>
        Wir zeigen und verarbeiten die mit Ihrem Zugang verknüpften Kontakt- und Unternehmensdaten,
        beispielsweise Name, E-Mail-Adresse, Position, Abteilung, Sprache, Unternehmen, Branche,
        Kunden- oder Partnerstatus, Support-Stufe und Portalberechtigungen. Zweck ist die Verwaltung
        der Geschäftsbeziehung und die Bereitstellung kontobezogener Funktionen. Rechtsgrundlage ist
        Art. 6 Abs. 1 lit. b DSGVO, ergänzend Art. 6 Abs. 1 lit. f DSGVO.
      </p>

      <h3>6.2 Community</h3>
      <p>
        Bei der Community-Nutzung verarbeiten wir Profildaten sowie von Ihnen erstellte Inhalte,
        insbesondere Beiträge, Antworten, Reaktionen, Tags, Moderationsvorgänge und hochgeladene
        Dateien. Inhalte können abhängig vom gewählten Bereich für andere berechtigte Portalnutzer
        sichtbar sein. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO. Moderations-, Audit- und
        Sicherheitsdaten verarbeiten wir auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.
      </p>

      <h3>6.3 Academy</h3>
      <p>
        Für den Zugriff auf die Academy verarbeiten wir Benutzerkennung, Name, E-Mail-Adresse,
        Rolle, Kontostatus und Berechtigungen. Die Wissensinhalte werden aus unserer
        Confluence-Umgebung synchronisiert. Eine personenbezogene Lernfortschritts- oder
        Prüfungsauswertung findet nach dem derzeitigen Funktionsumfang nicht statt. Rechtsgrundlage
        ist Art. 6 Abs. 1 lit. b DSGVO.
      </p>

      <h3>6.4 LaunchPad</h3>
      <p>
        Im LaunchPad verarbeiten wir die von Ihnen angelegten Prozess- und Automatisierungsdaten,
        etwa Bezeichnungen, verantwortliche Personen, Prozessbeschreibungen, Bewertungen, Notizen,
        Abhängigkeiten und hochgeladene Dokumente, Bilder, Audio-, Video- oder Bildschirmaufnahmen.
        Zusätzlich speichern wir die geschäftliche E-Mail-Adresse des Erstellers bzw. Bearbeiters
        und technische Metadaten zu Dateien. Zweck ist die von Ihnen gewünschte Erfassung, Bewertung
        und Bearbeitung von Automatisierungsvorhaben. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b
        DSGVO.
      </p>

      <h3>6.5 Support</h3>
      <p>
        Bei Supportanfragen verarbeiten wir insbesondere Name, geschäftliche Kontaktdaten, Betreff,
        Beschreibung, Kategorie, Priorität, Nachrichten, Status, Zeitstempel und Anhänge. Die
        Angaben werden zur Bearbeitung der Anfrage und zur Kommunikation mit Ihnen verwendet.
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO; für Qualitätskontrolle, Missbrauchsabwehr
        und Dokumentation Art. 6 Abs. 1 lit. f DSGVO. Supportfälle und Nachrichten werden mit
        HubSpot synchronisiert.
      </p>

      <h3>6.6 Shop und Bestellanfragen</h3>
      <p>
        Der Shop verarbeitet Kontaktdaten, Unternehmens- und Partnerkontext, Berechtigungen,
        kundenspezifische Konditionen sowie Produkt, Menge, Laufzeit, Notizen und den betroffenen
        Account einer Bestellanfrage. Die Daten werden zur Angebotserstellung, Vertragsdurchführung
        und Kommunikation verwendet. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO. Handels- und
        steuerrechtlich relevante Unterlagen verarbeiten wir zusätzlich nach Art. 6 Abs. 1 lit. c
        DSGVO.
      </p>

      <aside className="legal-note">
        Bitte übermitteln Sie in Freitextfeldern oder Uploads keine besonderen Kategorien
        personenbezogener Daten im Sinne des Art. 9 DSGVO, sofern dies für den jeweiligen Vorgang
        nicht zwingend erforderlich und mit uns abgestimmt ist.
      </aside>
    </section>

    <section className="legal-section" aria-labelledby="storage-heading">
      <h2 id="storage-heading">7. Cookies und lokaler Speicher</h2>
      <p>
        Das Portal und die eingebundenen Dienste verwenden technisch erforderliche Sitzungs- und
        Sicherheitscookies, insbesondere für Anmeldung, Sitzungsverwaltung und CSRF-Schutz. Außerdem
        können Einstellungen wie Sprache, Farbschema, Cookie-Auswahl und Bedienpositionen lokal im
        Browser gespeichert werden. Optionale Technologien werden nur entsprechend Ihrer Auswahl
        verwendet.
      </p>
      <p>
        Einzelheiten zu Namen, Zweck und Speicherdauer sowie eine Möglichkeit zur Änderung Ihrer
        Auswahl finden Sie in unserer <Link to="/cookie-policy">Cookie-Richtlinie</Link>.
      </p>
    </section>

    <section className="legal-section" aria-labelledby="recipients-heading">
      <h2 id="recipients-heading">8. Empfänger und eingesetzte Dienstleister</h2>
      <p>
        Innerhalb unseres Unternehmens erhalten nur diejenigen Stellen Zugriff, die ihn für die
        genannten Zwecke benötigen. Darüber hinaus können folgende Empfänger oder Kategorien von
        Empfängern Daten erhalten:
      </p>
      <ul className="legal-list">
        <li>
          <strong>Microsoft Ireland Operations Limited:</strong> Cloud-Hosting, Datenbanken,
          Dateiablage, Schlüsselverwaltung, Sicherungen und Betriebsüberwachung in Microsoft Azure.
        </li>
        <li>
          <strong>HubSpot Germany GmbH</strong>, Am Postbahnhof 17, 10243 Berlin, sowie verbundene
          HubSpot-Unternehmen: CRM, Kontakt- und Unternehmenskontext, Berechtigungsprüfung,
          Supportbearbeitung und Bestellkommunikation.
        </li>
        <li>
          <strong>Atlassian Pty Ltd und verbundene Unternehmen:</strong> Bereitstellung der in der
          Academy veröffentlichten Confluence-Wissensinhalte. Der Abruf erfolgt grundsätzlich über
          unsere serverseitige Integration.
        </li>
        <li>
          <strong>Google Ireland Limited</strong>, Gordon House, Barrow Street, Dublin 4, Irland:
          Einzelne Portal-Oberflächen rufen Schriftarten oder Symbolschriften von Google Fonts ab.
          Dabei werden insbesondere IP-Adresse, Browser- und Geräteinformationen sowie die
          angeforderte Ressource an Google übermittelt. Zweck ist die einheitliche Darstellung der
          Benutzeroberfläche; Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes
          Interesse liegt in einer technisch zuverlässigen und einheitlichen Darstellung. Eine
          Verarbeitung in den USA ist möglich und richtet sich nach Abschnitt 9.
        </li>
        <li>
          <strong>E-Mail-Dienstleister:</strong> Versand von Zugangseinladungen, Anmeldehinweisen,
          Support- und Bestellkommunikation.
        </li>
        <li>
          <strong>Berater, Behörden und Gerichte:</strong> soweit dies gesetzlich vorgeschrieben
          oder zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist.
        </li>
      </ul>
      <p>
        Auftragsverarbeiter werden nach Art. 28 DSGVO vertraglich verpflichtet und dürfen Daten nur
        nach unserer Weisung verarbeiten.
      </p>
    </section>

    <section className="legal-section" aria-labelledby="transfers-heading">
      <h2 id="transfers-heading">9. Datenübermittlungen in Drittländer</h2>
      <p>
        Bei der Nutzung internationaler Anbieter kann eine Verarbeitung außerhalb des Europäischen
        Wirtschaftsraums, insbesondere in den USA, nicht vollständig ausgeschlossen werden. Eine
        Übermittlung erfolgt nur, wenn die Voraussetzungen der Art. 44 ff. DSGVO erfüllt sind,
        insbesondere aufgrund eines Angemessenheitsbeschlusses der Europäischen Kommission (bei
        US-Empfängern gegebenenfalls EU-US Data Privacy Framework), geeigneter Garantien wie den
        EU-Standardvertragsklauseln oder einer gesetzlichen Ausnahme. Soweit erforderlich prüfen wir
        zusätzliche Schutzmaßnahmen.
      </p>
    </section>

    <section className="legal-section" aria-labelledby="retention-heading">
      <h2 id="retention-heading">10. Speicherdauer und Löschung</h2>
      <p>
        Wir speichern personenbezogene Daten nur so lange, wie es für den jeweiligen Zweck
        erforderlich ist. Kontodaten werden grundsätzlich für die Dauer des Portalzugangs
        gespeichert. Von Ihnen erstellte Portal-Inhalte bleiben bis zur Löschung, Anonymisierung
        oder Beendigung des jeweiligen Dienstes gespeichert, soweit keine Aufbewahrungs- oder
        Nachweispflichten entgegenstehen. Sitzungsdaten werden nach Ablauf der Sitzung bzw. der
        festgelegten kurzen Gültigkeitsdauer gelöscht. Produktionsprotokolle werden grundsätzlich
        nach 30 Tagen gelöscht.
      </p>
      <p>
        Nach Zweckerreichung löschen oder anonymisieren wir Daten, sofern handels-, steuer- oder
        andere gesetzliche Aufbewahrungspflichten sowie die Sicherung von Rechtsansprüchen einer
        Löschung nicht entgegenstehen. Für diese Zeit wird die Verarbeitung auf den jeweiligen Zweck
        beschränkt. Sicherungskopien werden im Rahmen der festgelegten Sicherungszyklen
        überschrieben.
      </p>
    </section>

    <section className="legal-section" aria-labelledby="rights-heading">
      <h2 id="rights-heading">11. Ihre Rechte</h2>
      <p>Sie haben nach Maßgabe der gesetzlichen Voraussetzungen insbesondere folgende Rechte:</p>
      <ul className="legal-list">
        <li>Auskunft über Ihre personenbezogenen Daten (Art. 15 DSGVO),</li>
        <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO),</li>
        <li>Löschung (Art. 17 DSGVO),</li>
        <li>Einschränkung der Verarbeitung (Art. 18 DSGVO),</li>
        <li>Datenübertragbarkeit (Art. 20 DSGVO),</li>
        <li>
          Widerspruch gegen Verarbeitungen nach Art. 6 Abs. 1 lit. e oder f DSGVO (Art. 21 DSGVO),
        </li>
        <li>Widerruf einer Einwilligung mit Wirkung für die Zukunft (Art. 7 Abs. 3 DSGVO).</li>
      </ul>
      <p>
        Zur Ausübung Ihrer Rechte können Sie sich an{' '}
        <a href="mailto:info@wianco.com">info@wianco.com</a> oder unseren Datenschutzbeauftragten
        wenden. Zur Vermeidung unbefugter Auskünfte können wir einen geeigneten Identitätsnachweis
        verlangen.
      </p>
      <h3>Widerspruchsrecht</h3>
      <p>
        Soweit wir Daten auf Grundlage berechtigter Interessen verarbeiten, können Sie aus Gründen,
        die sich aus Ihrer besonderen Situation ergeben, jederzeit Widerspruch einlegen. Wir
        verarbeiten die Daten dann nicht mehr, es sei denn, wir können zwingende schutzwürdige
        Gründe nachweisen oder die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung
        von Rechtsansprüchen.
      </p>
      <h3>Beschwerderecht</h3>
      <p>
        Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren, insbesondere
        in dem Mitgliedstaat Ihres Aufenthaltsorts, Ihres Arbeitsplatzes oder des Orts des
        mutmaßlichen Verstoßes. Für uns zuständig ist der{' '}
        <a href="https://datenschutz.hessen.de/" target="_blank" rel="noreferrer">
          Hessische Beauftragte für Datenschutz und Informationsfreiheit
        </a>
        .
      </p>
    </section>

    <section className="legal-section" aria-labelledby="decisions-heading">
      <h2 id="decisions-heading">12. Automatisierte Entscheidungen</h2>
      <p>
        Eine ausschließlich automatisierte Entscheidungsfindung einschließlich Profiling im Sinne
        des Art. 22 DSGVO findet im Portal nicht statt. Die automatisierte Zuordnung von
        Portalrollen anhand des hinterlegten Kunden- oder Partnerstatus dient ausschließlich der
        technischen Zugriffssteuerung. Wenn ein gesetzlich relevanter Einzelfall künftig
        automatisiert entschieden werden sollte, informieren wir Sie gesondert.
      </p>
    </section>

    <section className="legal-section" aria-labelledby="changes-heading">
      <h2 id="changes-heading">13. Änderungen und Fragen</h2>
      <p>
        Wir aktualisieren diese Datenschutzerklärung, wenn sich Rechtslage, Portal-Funktionen oder
        Verarbeitungen ändern. Die jeweils aktuelle Fassung ist dauerhaft im Portal verfügbar. Bei
        Fragen oder Hinweisen erreichen Sie uns über die oben genannten Kontaktdaten.
      </p>
    </section>
  </>
);

const EnglishPolicy = () => (
  <>
    <section className="legal-section">
      <h2>1. Scope and introduction</h2>
      <p>
        This privacy notice explains, in accordance with Articles 12 to 14 GDPR, how personal data
        is processed when you use the WIANCO Portal at portal.ottrobotics.de, including Community,
        Academy, LaunchPad, Support and Shop.
      </p>
      <p>
        Personal data is any information relating to an identified or identifiable natural person,
        such as a name, email address, user identifier or IP address.
      </p>
    </section>

    <section className="legal-section">
      <h2>2. Controller and data protection officer</h2>
      <h3>2.1 Controller</h3>
      <ContactDetails language="en" />
      <p>Represented by managing directors Michael Wilczynska and Paul Tessmann.</p>
      <h3>2.2 Data protection officer</h3>
      <DataProtectionOfficer language="en" />
    </section>

    <section className="legal-section">
      <h2>3. Purposes and legal bases</h2>
      <p>We process personal data only where a legal basis applies:</p>
      <ul className="legal-list">
        <li>
          <strong>Consent (Article 6(1)(a) GDPR):</strong> for features requiring consent. Consent
          may be withdrawn at any time with future effect.
        </li>
        <li>
          <strong>Contract and pre-contractual steps (Article 6(1)(b) GDPR):</strong> to provide the
          Portal account, contracted services, support and order requests.
        </li>
        <li>
          <strong>Legal obligation (Article 6(1)(c) GDPR):</strong> in particular for commercial,
          tax or regulatory requirements.
        </li>
        <li>
          <strong>Legitimate interests (Article 6(1)(f) GDPR):</strong> for secure and stable
          operation, abuse prevention, troubleshooting, access management, customer care and legal
          claims.
        </li>
      </ul>
      <p>
        Storage of or access to information on your device is governed by section 25 TDDDG. Strictly
        necessary operations rely on section 25(2)(2) TDDDG; otherwise, we obtain prior consent
        under section 25(1) TDDDG.
      </p>
    </section>

    <section className="legal-section">
      <h2>4. Portal access, hosting and log data</h2>
      <p>
        Each request sends technically necessary data to our systems. This may include your IP
        address, date and time, requested URL, referrer URL, HTTP status, transferred volume,
        browser type and version, operating system, language and device information.
      </p>
      <p>
        We process this data to deliver the Portal, maintain stability and security, diagnose errors
        and prevent abuse under Article 6(1)(f) GDPR. Production operational telemetry is generally
        retained for up to 30 days. Data relevant to a security incident may be retained until the
        incident and related legal claims have been resolved.
      </p>
      <p>
        The Portal is hosted on Microsoft Azure in European Union data centres, using Azure
        Kubernetes Service, Azure Database for PostgreSQL, Azure Files, Azure Blob Storage, Azure
        Key Vault and Azure Monitor/Log Analytics. Our processor is Microsoft Ireland Operations
        Limited, One Microsoft Place, South County Business Park, Leopardstown, Dublin 18, Ireland.
      </p>
    </section>

    <section className="legal-section">
      <h2>5. Account, authentication and permissions</h2>
      <p>
        We use a self-operated Keycloak instance for authentication and access control. We process
        user ID, name, business email address, hashed credentials, roles, groups, permissions,
        session and token data, login times and security events to authenticate you, enforce access
        rights and protect your account.
      </p>
      <p>
        We match your business email and contact, company, customer and partner status with HubSpot
        to determine and refresh Portal permissions. For first-time access, we also use your email
        to check eligibility and send an invitation or sign-in message. The legal basis is Article
        6(1)(b) GDPR and, for additional permission and abuse controls, Article 6(1)(f) GDPR.
      </p>
      <p>
        Required fields are identifiable as such. We cannot provide Portal access without the data
        needed for authentication and permission checks.
      </p>
      <p>
        We obtain account and permission data from you, the organisation that arranged your access,
        and our Keycloak identity and HubSpot CRM systems.
      </p>
    </section>

    <section className="legal-section">
      <h2>6. Processing within Portal services</h2>
      <h3>6.1 Profile and account context</h3>
      <p>
        We process contact and company data linked to your account, such as name, email, job title,
        department, language, company, industry, customer or partner status, support tier and Portal
        permissions. This supports the business relationship and account-specific features under
        Article 6(1)(b), supplemented by Article 6(1)(f), GDPR.
      </p>
      <h3>6.2 Community</h3>
      <p>
        We process profile data and content you create, including posts, replies, reactions, tags,
        moderation actions and uploaded files. Depending on the selected space, content may be
        visible to other authorised Portal users. Article 6(1)(b) GDPR applies; moderation, audit
        and security data is processed under Article 6(1)(f) GDPR.
      </p>
      <h3>6.3 Academy</h3>
      <p>
        We process your user ID, name, email, role, account status and permissions to provide
        Academy access. Knowledge content is synchronised from our Confluence environment. The
        current feature set does not perform personal learning-progress or examination analysis. The
        legal basis is Article 6(1)(b) GDPR.
      </p>
      <h3>6.4 LaunchPad</h3>
      <p>
        We process the process and automation data you create, including titles, owners, process
        descriptions, assessments, notes, dependencies and uploaded documents, images, audio, video
        or screen recordings. We also store the creator/editor's business email and technical file
        metadata. This provides the requested capture, assessment and management workflow under
        Article 6(1)(b) GDPR.
      </p>
      <h3>6.5 Support</h3>
      <p>
        Support requests may include your name, business contact details, subject, description,
        category, priority, messages, status, timestamps and attachments. We use this data to handle
        and communicate about your request under Article 6(1)(b) GDPR, and for quality, abuse
        prevention and documentation under Article 6(1)(f) GDPR. Support cases and messages are
        synchronised with HubSpot.
      </p>
      <h3>6.6 Shop and order requests</h3>
      <p>
        Shop processes contact data, company and partner context, permissions, account-specific
        terms, and the product, quantity, term, notes and affected account in an order request. This
        supports quotations, contract performance and communication under Article 6(1)(b) GDPR.
        Commercial and tax records are additionally processed under Article 6(1)(c) GDPR.
      </p>
      <aside className="legal-note">
        Do not submit special categories of personal data under Article 9 GDPR in free-text fields
        or uploads unless strictly necessary for the relevant matter and agreed with us.
      </aside>
    </section>

    <section className="legal-section">
      <h2>7. Cookies and local storage</h2>
      <p>
        The Portal and embedded services use technically necessary session and security cookies for
        authentication, session management and CSRF protection. Preferences such as language, theme,
        cookie selection and control positions may be stored locally in your browser. Optional
        technologies are used only according to your selection.
      </p>
      <p>
        Names, purposes, retention periods and controls are described in our{' '}
        <Link to="/cookie-policy">Cookie Policy</Link>.
      </p>
    </section>

    <section className="legal-section">
      <h2>8. Recipients and service providers</h2>
      <p>
        Access within our company is limited to personnel who require it for the purposes described.
        Data may also be disclosed to the following recipients or recipient categories:
      </p>
      <ul className="legal-list">
        <li>
          <strong>Microsoft Ireland Operations Limited:</strong> Azure cloud hosting, databases,
          file storage, key management, backups and operational monitoring.
        </li>
        <li>
          <strong>HubSpot Germany GmbH</strong>, Am Postbahnhof 17, 10243 Berlin, and affiliated
          HubSpot companies: CRM, contact and company context, permission checks, support and order
          communication.
        </li>
        <li>
          <strong>Atlassian Pty Ltd and affiliates:</strong> Confluence knowledge content published
          in Academy, generally retrieved through our server-side integration.
        </li>
        <li>
          <strong>Google Ireland Limited</strong>, Gordon House, Barrow Street, Dublin 4, Ireland:
          some Portal interfaces retrieve fonts or icon fonts from Google Fonts. This discloses your
          IP address, browser and device information and the requested resource to Google. The
          purpose is a consistent user interface under Article 6(1)(f) GDPR, based on our interest
          in a technically reliable and consistent presentation. Processing in the United States is
          possible and is addressed in section 9.
        </li>
        <li>
          <strong>Email service providers:</strong> access invitations, login notices, support and
          order communication.
        </li>
        <li>
          <strong>Advisers, authorities and courts:</strong> where legally required or necessary for
          legal claims.
        </li>
      </ul>
      <p>Processors are bound by Article 28 GDPR and may process data only on our instructions.</p>
    </section>

    <section className="legal-section">
      <h2>9. Transfers outside the EEA</h2>
      <p>
        International providers may process data outside the European Economic Area, particularly in
        the United States. Transfers take place only under Articles 44 et seq. GDPR, including an
        adequacy decision (where applicable, the EU-US Data Privacy Framework), appropriate
        safeguards such as EU Standard Contractual Clauses, or a statutory exception. Additional
        safeguards are assessed where required.
      </p>
    </section>

    <section className="legal-section">
      <h2>10. Retention and deletion</h2>
      <p>
        We retain personal data only as long as required for its purpose. Account data is generally
        kept for the duration of Portal access. User-created content remains until deletion,
        anonymisation or termination of the relevant service unless retention or evidentiary
        obligations apply. Session data expires after the session or its short validity period.
        Production logs are generally deleted after 30 days.
      </p>
      <p>
        Once the purpose ends, data is deleted or anonymised unless commercial, tax or other
        statutory retention duties or legal claims require restricted retention. Backups are
        overwritten within the configured backup cycles.
      </p>
    </section>

    <section className="legal-section">
      <h2>11. Your rights</h2>
      <p>Subject to the statutory conditions, you have the right to:</p>
      <ul className="legal-list">
        <li>access your personal data (Article 15 GDPR),</li>
        <li>rectify inaccurate data (Article 16 GDPR),</li>
        <li>erasure (Article 17 GDPR),</li>
        <li>restriction of processing (Article 18 GDPR),</li>
        <li>data portability (Article 20 GDPR),</li>
        <li>object to processing under Article 6(1)(e) or (f) GDPR (Article 21 GDPR), and</li>
        <li>withdraw consent with future effect (Article 7(3) GDPR).</li>
      </ul>
      <p>
        Contact <a href="mailto:info@wianco.com">info@wianco.com</a> or our data protection officer
        to exercise your rights. We may request appropriate proof of identity to prevent
        unauthorised disclosure.
      </p>
      <h3>Right to object</h3>
      <p>
        Where processing relies on legitimate interests, you may object at any time on grounds
        relating to your particular situation. We will cease processing unless we demonstrate
        compelling legitimate grounds or the processing is required for legal claims.
      </p>
      <h3>Right to lodge a complaint</h3>
      <p>
        You may lodge a complaint with a data protection supervisory authority, particularly in the
        EU Member State of your residence, workplace or the alleged infringement. Our competent
        authority is the{' '}
        <a href="https://datenschutz.hessen.de/" target="_blank" rel="noreferrer">
          Hessian Commissioner for Data Protection and Freedom of Information
        </a>
        .
      </p>
    </section>

    <section className="legal-section">
      <h2>12. Automated decisions</h2>
      <p>
        The Portal does not make decisions based solely on automated processing, including
        profiling, within Article 22 GDPR. Automated Portal-role assignment based on stored customer
        or partner status is used only for technical access control. We will provide separate notice
        if a legally relevant individual decision is automated in the future.
      </p>
    </section>

    <section className="legal-section">
      <h2>13. Changes and questions</h2>
      <p>
        We update this notice when applicable law, Portal features or processing changes. The
        current version remains available in the Portal. Contact us using the details above with any
        questions or comments.
      </p>
    </section>
  </>
);

const PrivacyPolicyPage: React.FC = () => {
  const { language, formatDate } = useShellI18n();
  const isGerman = language === 'de';

  return (
    <main className="public-legal-route">
      <div className="legal-route-nav">
        <Link to="/" className="btn ghost sm">
          {isGerman ? 'Zum Portal' : 'Back to Portal'}
        </Link>
      </div>
      <div className="legal-page">
        <header className="page-hero">
          <p className="auth-kicker">WIANCO Portal</p>
          <h1 className="page-title">
            {isGerman ? 'Datenschutz und Privatsphäre' : 'Data Protection and Privacy'}
          </h1>
          <p className="page-subtitle">
            {isGerman
              ? 'Informationen zur Verarbeitung personenbezogener Daten im WIANCO Portal.'
              : 'Information about the processing of personal data in the WIANCO Portal.'}
          </p>
          <p className="meta">
            {isGerman ? 'Stand' : 'Effective date'}: {formatDate(EFFECTIVE_DATE)}
          </p>
        </header>

        {isGerman ? <GermanPolicy /> : <EnglishPolicy />}
      </div>
    </main>
  );
};

export default PrivacyPolicyPage;
